import { BRAIN, STATE, TAU } from '../actors/ai.js';
import { hashSeed, mulberry32 } from '../math/rng.js';

/**
 * @file THE SHOP — the people in it, and the catalogue they sell from.
 *
 * A storefront's fixtures are BAKED. They have to be: lighting in this project
 * is CPU-baked into vertex colours at build time and merged geometry is never
 * mutated at runtime, so a shelf that did not exist when the level was built
 * can never exist at all. `levels/city/interiors.js` therefore ships every
 * room's counter, stock and stand points with the city.
 *
 * The PEOPLE are the one part that can arrive late, because an actor is a slot
 * in a fixed-cap pool rather than a piece of geometry — and that is the whole
 * optimisation this file exists for:
 *
 * ```
 *   player on the street   → not one shop NPC exists anywhere
 *   player walks through a door → THAT room's crowd is spawned, and only it
 *   player leaves (or dies, or the level tears down) → the crowd is gone
 * ```
 *
 * Eighty shopfronts' worth of ambient crowd is eighty crowds we never pay for.
 * The trigger is `interiors.plan` compared BY IDENTITY: a new plan populates, a
 * null clears, and the same plan two steps running is one comparison and no
 * work.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * NO THREE.JS
 * ─────────────────────────────────────────────────────────────────────────────
 * Same mould as `systems/Interiors.js` and `systems/Carjack.js`: the manager,
 * the door system, the wallet, the player and the bus are all INJECTED, so the
 * whole lifecycle — every populate, every refusal, every abort — is provable in
 * node with duck types. `actors/ai.js` and `math/rng.js` are the only imports
 * and both are plain arithmetic.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE TILL IS A CATALOGUE NOW, AND NOTHING HERE KNOWS WHAT A GROCER IS
 * ─────────────────────────────────────────────────────────────────────────────
 * The till used to be one item per kind and a key that bought it. It is a
 * PANEL now — React renders the rows, this file is the model — and the whole
 * shape of the rewrite is one rule:
 *
 * > **NO FUNCTION IN THIS FILE MAY BRANCH ON A SPECIFIC KIND NAME OR A SPECIFIC
 * > EFFECT TYPE.** Adding a weapons dealer or a clothing boutique is a
 * > {@link SHOP_CATALOGUE} entry plus, at most, one {@link SHOP_EFFECTS} entry.
 * > It is never a change to control flow in here.
 *
 * Two injected tables carry everything that used to be a branch:
 *
 * * **{@link SHOP_CATALOGUE}** — what each trade sells: a title and a list of
 *   items, each item a price and a list of EFFECT SPECS (`{type, ...}`). The
 *   catalogue is gameplay's, never the level's: the level publishes geometry and
 *   stand points, and this table decides what a gun shop is FOR.
 * * **{@link SHOP_EFFECTS}** — the open VERB SET: one entry per effect `type`,
 *   holding the four functions that turn a spec into a row and a purchase into a
 *   change in the world. An effect type this table does not know is not an
 *   error; see the forward-compat idiom below.
 *
 * Both are `createShops` options, and the suites and dev scenes inject
 * FABRICATED kinds and FABRICATED effect types. That injection is the PROOF
 * that nothing is hard-coded, so it is a real seam and must stay one.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ⚠ FORWARD COMPAT: AN UNDELIVERABLE ROW IS ABSENT, NEVER BROKEN
 * ─────────────────────────────────────────────────────────────────────────────
 * The old code carried this idiom in one line — armour was gated on
 * `typeof player.addArmour === 'function'` as well as on the number, because
 * until `actors/Player.js` grew the method there was nowhere to put the armour
 * and offering a purchase this file cannot apply is a broken promise. That line
 * is now the RULE, generalised:
 *
 * ```
 *   an effect is DELIVERABLE  <=>  the registry knows its `type`
 *                              AND that entry has an `apply`
 *                              AND its optional `available(player, e, ctx)`
 *                                  does not say otherwise
 *   a row with NO deliverable effect is DROPPED FROM THE SNAPSHOT ENTIRELY
 * ```
 *
 * Dropped, ⚠ **not shown greyed** — an item the game cannot deliver must never
 * appear at all, because a row on screen is a promise the same way a prompt is.
 * A row whose effects are only PARTLY deliverable keeps the deliverable ones and
 * neither describes nor applies the rest, so the player is never promised the
 * half that does not exist yet.
 *
 * That is what lets a `weapon` or an `outfit` effect be AUTHORED IN THE
 * CATALOGUE TODAY, before `Player` grows the method that would apply it: the
 * rows simply are not there, the shop still opens on everything else, and the
 * day the method lands the boutique starts selling with no change here.
 *
 * ⚠ Note the two-part split: **`available` DROPS the row, `can` GREYS it.**
 * `armour.can` still carries its own `typeof player.addArmour` check as belt and
 * braces (an `apply` must never be called into a void), but it is `available`
 * that decides the row exists — collapse the two and "not implemented yet" and
 * "your vest is full" become the same thing on screen.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE OWNERSHIP SEAM IS SPENT: `loadout` HOLDS THE WEAPONS SYSTEM
 * ─────────────────────────────────────────────────────────────────────────────
 * `loadout` was a construction option reserved for a future inventory and null
 * in every shipped configuration. It is not reserved any more: `Gameplay` hands
 * the WEAPONS system in through it, and the shipped `ammo` verb reaches the
 * ammo pouch through `ctx.loadout` and nothing else. The seam did its job — the
 * verb that spends it is a table entry, and not one line of control flow in
 * here changed to accept it.
 *
 * ⚠ The seam is still OPTIONAL, and that is what keeps every other caller
 * working. Hero mode, every dev scene, every suite fixture and `/play/shopui`
 * build a shop with no `loadout` at all; there `ammo.available` is false, the
 * row is ABSENT, the gun shop is a two-row shop again, and every other row
 * opens exactly as before. That is the `available` DROPS / `can` GREYS rule
 * doing the forward-compat job it was written for.
 *
 * ⚠ `'owned'` is STILL produced by nothing shipped. It is in the row-state
 * union, ranked above `'full'` in the precedence table, and the panel can draw
 * it today — but the one verb that touches the loadout treats a full pouch as a
 * `'full'` refusal, because a box of rounds is a CONSUMABLE and "you already
 * own ammunition" is not a sentence. A real ownership verb is still
 *
 * ```js
 *   weapon: {
 *     available: (p) => typeof p?.giveWeapon === 'function',
 *     can:       (p, e, ctx) => !ctx.loadout?.has(e.id),
 *     state:     'owned',                       // what a false `can` reads as
 *     full:      'Already in your loadout',
 *     apply:     (p, e) => p.giveWeapon(e.id),
 *     describe:  (e) => e.id,
 *   }
 * ```
 *
 * ⚠ With `loadout` null such a row's `can` is always true, so it never reads as
 * owned and it never appears as owned; a null loadout means the state simply
 * does not occur. `tests/shops.test.mjs` section (m) is the only place it
 * exists.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE TRADE KEY: `plan.trade ?? plan.kind`
 * ─────────────────────────────────────────────────────────────────────────────
 * The catalogue is read with `plan.trade ?? plan.kind`. ⚠ `trade` does not exist
 * on a plan today and must NOT be added to `levels/city/interiors.js` — that
 * file has a hard 99-DICE-PER-ROOM contract and one extra draw moves the lamps
 * in every room in the level.
 *
 * It is a documented FORWARD SEAM, and what it buys is the separation of the
 * FIXTURE SET from the CATALOGUE. `kind` is what the room is BUILT as — the
 * gunsmith's glazed case, the diner's stools — and it is baked. `trade` would be
 * what it SELLS. A pawnbroker that reuses the gunsmith's fixtures is then a
 * catalogue entry and one optional field on a plan, at ZERO cost to level
 * generation and zero risk to the dice. Until something sets it, `?? plan.kind`
 * makes this line byte-for-byte the old behaviour.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * SIX THINGS THAT WILL BITE
 * ─────────────────────────────────────────────────────────────────────────────
 * • **`clear()` must survive `Interiors#abort()`.** Abort is called on the
 *   player's death and on teardown, and it drops `plan` with no warning and no
 *   event. There is no "leaving" hook to hang the cleanup on — the identity
 *   watch in {@link update} IS the hook, and it is why `clear()` is written to
 *   be correct and idempotent from a standing start. A shopkeeper left walking
 *   the city 100 m under the pavement is this system's only real failure mode.
 *
 * • **A SLOT THAT DIED MUST NOT BE RESURRECTED.** Despawning a slot twice is
 *   already harmless (`EnemyManager#despawn` no-ops on a free slot), but a slot
 *   that was freed and then RE-SPAWNED by the population director is a
 *   different person standing in the street, and despawning that one is a
 *   civilian deleted out of the world. Polling `isLive()` cannot see it: the
 *   free and the re-spawn both happen inside one fixed step, between our
 *   updates, and by the time we look the slot is live again. So we CHAIN
 *   `manager.onDespawn` (capture the previous handler, call it, restore it in
 *   `dispose()` — and only if nobody has reassigned it since). The chain is the
 *   only seam that fires on the step the slot actually goes free.
 *
 * • **Every customer's dice are rolled even for a customer we then drop.** Same
 *   discipline as `levels/city/interiors.js`'s fixed dice-per-room: a room with
 *   two browse stations still rolls all three customers in full and drops the
 *   third afterwards, so raising {@link SHOP}`.maxCustomers` — or a level giving
 *   a room a third stand point — cannot reshuffle the two customers that were
 *   already there.
 *
 * • **THE PROMPT AND THE KEY MUST AGREE.** `docs/interiors.md`: "a prompt for a
 *   key that refuses is worse than no prompt." Every condition that yields
 *   `null` or a refusal string also makes {@link press} return false, and they
 *   share ONE `evaluate()` rather than each testing the world separately. That
 *   is structural, not a pair of conditions somebody has to keep in step:
 *   `prompt`, `canOpen`, `open`, `press` and `buy` are five callers of one
 *   function.
 *
 * • ⚠ **THE SUPPRESSION RULE INVERTS FOR ROWS, AND A FUTURE READER WILL TRY TO
 *   "RESTORE" IT.** The old till showed NO prompt when the item would do nothing
 *   (full health, full vest) or could not be afforded — correct, because the
 *   prompt WAS the offer and there was exactly one item. A PANEL is the
 *   opposite: it opens on a shop where every row is unaffordable or useless,
 *   and it shows WHY row by row (`'poor'` with a price, `'full'` with a reason).
 *   That legibility is the entire reason the panel exists — "you cannot afford
 *   the vest and you are already at full health" is information the old design
 *   could only express as silence.
 *
 *   What did NOT invert is the KEEPER. A dead keeper is still a refusal, still
 *   `'Nobody is serving'`, and the panel then never opens at all — which is why
 *   keeper state never has to appear inside it. The one thing that also refuses
 *   is a catalogue with no RENDERABLE row at all (see the forward-compat idiom):
 *   a shop that can offer nothing is a shop with nothing to open.
 *
 * • ⚠ **A PANEL LEFT ON SCREEN OVER A TORN-DOWN WORLD IS UNRECOVERABLE.** The
 *   React panel freezes the sim with `loop.setTimeScale(0)`, exactly as
 *   `ui/hud/Cutscene.jsx` does, and that file records finding the hard way what
 *   happens when a scene ends by anything other than a click: a still image over
 *   a frozen loop with nothing left to answer it. So `'shop:close'` is a TWO-WAY
 *   channel here too — every path that invalidates the room emits it
 *   ({@link clear}, which `dispose()` and the identity watch both route
 *   through), and the token is what lets a stale answer be rejected.
 *
 *   ⚠ And because the sim is frozen while the panel is open, `update()` is NOT
 *   running: anything a purchase changes must be republished SYNCHRONOUSLY from
 *   {@link buy}. A state refresh deferred to the next step never happens.
 *
 * • **Shop NPCs are NOT the population director's.** It only culls slots its
 *   own `owned[]` byte marks, and it never marks ours — so a customer standing
 *   100 m under the city is safe from a distance cull that would otherwise
 *   delete the entire crowd on the first step. Do not register them with it.
 *   They are ordinary civilians in every other respect: the hero auto-attack
 *   only ever fires at a faction that is hostile to the player, and a civilian
 *   can never become one, so it will not fire at them. ⚠ Since 2026-08-07 that
 *   is the WHOLE story — the deliberate SPACE shot that used to be able to kill
 *   one on purpose is deleted, so a shop customer is currently unkillable. When
 *   a manual shot comes back, shooting one must be the same crime as shooting
 *   one in the street; that needs no special case here either, and adding one
 *   would be the bug.
 *
 * @module engine/systems/Shops
 */

/** @typedef {import('./Interiors.js').InteriorPlan} InteriorPlan */

/**
 * @typedef {Object} ShopEffectSpec One thing an item DOES, as authored data.
 *   `type` names a {@link SHOP_EFFECTS} verb; every other field belongs to that
 *   verb and this file never reads one.
 * @property {string} type
 */

/**
 * @typedef {Object} ShopItem One row of a trade's catalogue.
 * @property {string} id STABLE — it is what `'shop:bought'` carries and what
 *   {@link createShops} `buy(id)` is called with.
 * @property {string} label
 * @property {number} price Finite, >= 0. An item without one cannot be sold and
 *   is dropped.
 * @property {ShopEffectSpec[]} effects One or more. A row with no DELIVERABLE
 *   effect is dropped from the snapshot — see the header.
 */

/**
 * @typedef {Object} ShopCatalogueEntry What one trade sells.
 * @property {string} title Shown at the top of the panel.
 * @property {ShopItem[]} items
 */

/**
 * @typedef {Object} ShopEffectVerb How to render and apply one effect TYPE. The
 *   open extension point: a new verb is a new key in {@link SHOP_EFFECTS} (or in
 *   an injected table) and no change to any control flow here.
 * @property {(player: any, e: ShopEffectSpec, ctx: ShopEffectCtx) => boolean} [can]
 *   Would buying this DO anything? Absent reads as "always". A false makes the
 *   row REFUSED (greyed), never absent.
 * @property {(player: any, e: ShopEffectSpec, ctx: ShopEffectCtx) => void} apply
 *   ⚠ REQUIRED, and its absence is one of the two ways to declare an effect
 *   undeliverable — a verb with no applier drops its row.
 * @property {(e: ShopEffectSpec) => string} [describe] One clause of the row's
 *   `effect` line. Deliverable effects only.
 * @property {string} [full] The reason shown when `can` is false.
 * @property {(player: any, e: ShopEffectSpec, ctx: ShopEffectCtx) => boolean} [available]
 *   ⚠ The OTHER way to declare undeliverable, and the one that reads as a
 *   forward seam: "the player cannot receive this yet". Anything but `true`
 *   drops the row. Absent reads as available.
 * @property {'full'|'owned'} [state] Which row state a false `can` reads as.
 *   Default `'full'`.
 */

/**
 * @typedef {Object} ShopEffectCtx Handed to every verb. Small, shared and
 *   FROZEN — a verb may read it and must not keep or mutate it.
 * @property {any} loadout The ownership seam, SPENT: `Gameplay` puts the
 *   weapons system here and the `ammo` verb reads the pouch through it. Still
 *   `null` wherever nobody passes one — every dev scene, every fixture — and a
 *   verb that needs it must therefore probe rather than assume. See the header.
 */

/**
 * @typedef {Object} ShopRow One rendered row. This is the ENTIRE contract with
 *   the React panel: it can draw a shop with zero knowledge of kinds, effect
 *   types, prices or affordability, because every one of those has already been
 *   resolved into a string or a state here.
 * @property {string} id
 * @property {string} label
 * @property {number} price
 * @property {string} effect The verbs' `describe()`s joined with `' · '`.
 * @property {'buy'|'poor'|'full'|'owned'} state
 * @property {string|null} reason `null` for `'buy'`; a price for `'poor'`; the
 *   verb's `full` string otherwise.
 */

/**
 * @typedef {Object} ShopSnapshot What the panel renders. Never null.
 * @property {string|null} token The open panel's token, or null.
 * @property {string|null} kind The TRADE key (`plan.trade ?? plan.kind`).
 * @property {string|null} title
 * @property {number} cash The wallet balance at the moment of the snapshot.
 * @property {ShopRow[]} items
 */

/** Every number the shop owns. Seconds and metres. */
export const SHOP = Object.freeze({
  /**
   * How close the player must stand to `stations.till` to trade. Tighter than
   * a door's 2.2 m mat on purpose: the till is a spot ON a counter the player
   * can walk the length of, and a wider reach would offer the sale from the
   * far end of the shop.
   */
  tillReach: 1.7,
  /** Customers per room, INCLUSIVE. Zero is a real outcome — an empty shop
   *  reads as a quiet afternoon, and a shop that is always busy reads as a
   *  spawner. */
  minCustomers: 0,
  maxCustomers: 3,
  /**
   * A customer moves to another browse station roughly this often. Long enough
   * that the room is not a fairground; short enough that a player who buys a
   * snack and looks up sees the place has changed.
   */
  browseDwell: 6.5,
  /** ...spread this far either side of it, per customer, so nobody moves in
   *  step with anybody else. */
  browseJitter: 3.0,
});

/**
 * Freezes a plain data tree. The catalogue is authored data that several
 * systems read and nobody may edit, and a shallow `Object.freeze` on the outer
 * object would leave every item and every effect spec writable.
 * @template T @param {T} v @returns {T}
 */
function deepFreeze(v) {
  if (v === null || typeof v !== 'object') return v;
  Object.freeze(v);
  for (const k of Object.keys(v)) deepFreeze(v[k]);
  return v;
}

/**
 * WHAT EACH TRADE SELLS, keyed by `plan.trade ?? plan.kind`. Gameplay's table,
 * never the level's.
 *
 * ⚠ The three ids `snack` / `meal` / `armour` are the ones the till already
 * shipped and they are STABLE: `'shop:bought'` payloads carry them, so renaming
 * one changes the meaning of an event somebody else may already be reading.
 * `sixpack` / `breakfast` / `vest` / `ammo` are new and free.
 *
 * ⚠ TWO ROWS PER TRADE IS AN ECONOMY DECISION and it still holds for the two
 * food trades: lifetime income is $1,150 of mission rewards plus a $500 start,
 * so a shop the player can never work through is a shop that lies. The gun
 * shop's THIRD row is not a third armour tier — it is a $25 CONSUMABLE, bought
 * over and over, which is a different economy from the two aspirational rows
 * above it. ⚠ The ladder inside a trade ASCENDS, so the cheap consumable goes
 * FIRST — and the panel opens its cursor on the first `'buy'` row, which is the
 * thing the player came back for.
 *
 * A new trade is a new key here. Nothing in this file learns its name.
 * @type {Readonly<Record<string, ShopCatalogueEntry>>}
 */
export const SHOP_CATALOGUE = deepFreeze({
  grocer: {
    title: 'Grocer',
    items: [
      { id: 'snack', label: 'Snack', price: 15, effects: [{ type: 'heal', amount: 25 }] },
      { id: 'sixpack', label: 'Six-pack', price: 35, effects: [{ type: 'heal', amount: 60 }] },
    ],
  },
  diner: {
    title: 'Diner',
    items: [
      { id: 'breakfast', label: 'Breakfast', price: 25, effects: [{ type: 'heal', amount: 50 }] },
      { id: 'meal', label: 'Hot meal', price: 40, effects: [{ type: 'heal', amount: 100 }] },
    ],
  },
  gunsmith: {
    title: 'Gunsmith',
    items: [
      { id: 'ammo', label: 'Ammo box', price: 25, effects: [{ type: 'ammo', amount: 60 }] },
      { id: 'vest', label: 'Light vest', price: 60, effects: [{ type: 'armour', amount: 50 }] },
      { id: 'armour', label: 'Body armour', price: 120, effects: [{ type: 'armour', amount: 100 }] },
    ],
  },
});

/**
 * THE VERB SET. One entry per effect `type`; see {@link ShopEffectVerb} for the
 * contract and the header for why `available` and `can` are two different
 * things.
 *
 * ⚠ Every shipped verb carries an `available` that probes for the thing it
 * would deliver INTO — a method on the player, or a whole system on
 * `ctx.loadout` — so a duck type that has neither (a dev scene, a suite
 * fixture) yields a shop with fewer rows, or none at all, rather than a shop
 * that takes money and does nothing.
 *
 * ⚠ APPEND, NEVER INSERT. The suite pins `Object.keys(...).join(',')`, which is
 * the ORDER as well as the set, and a verb slipped into the middle is a diff
 * that reads as a rewrite.
 * @type {Readonly<Record<string, ShopEffectVerb>>}
 */
export const SHOP_EFFECTS = Object.freeze({
  heal: Object.freeze({
    available: (player) => typeof player?.hurt === 'function',
    can: (player) => (player?.hp ?? 0) < (player?.maxHp ?? 0),
    // Healing IS negative damage — one arithmetic path, so a heal can never
    // find a branch `hurt()` does not have (armour, death, the respawn clamp).
    apply: (player, e) => player.hurt(-e.amount),
    describe: (e) => `+${e.amount} health`,
    full: 'Already at full health',
  }),
  armour: Object.freeze({
    available: (player) => typeof player?.addArmour === 'function',
    // Belt and braces: `available` has already dropped the row if the method is
    // missing, and this second check is what makes `apply` impossible to reach
    // through an injected table that forgot one.
    can: (player) => typeof player?.addArmour === 'function'
      && (player?.armour ?? 0) < (player?.maxArmour ?? 100),
    apply: (player, e) => player.addArmour(e.amount),
    describe: (e) => `+${e.amount} armour`,
    full: 'Vest already full',
  }),
  ammo: Object.freeze({
    // ⚠ The ONLY verb that delivers into `ctx.loadout` rather than into the
    // player, and the only one whose `available` can be false in a fully built
    // game: hero mode, the dev scenes and the suites' fixtures all construct a
    // shop with no weapons system, and there the row is simply ABSENT. Probing
    // for the METHOD rather than for the object is what makes a partially built
    // loadout drop the row instead of throwing inside `apply`.
    available: (player, e, ctx) => typeof ctx?.loadout?.addAmmo === 'function',
    // ⚠ ASK THE MODEL, never arithmetic on a reserve and a cap read from here.
    // Two places computing "is the pouch full" is exactly the bug the panel's
    // no-affordability-of-its-own rule exists to prevent, one layer down.
    can: (player, e, ctx) => ctx?.loadout?.roomForAmmo?.() === true,
    // ⚠ A PARTIAL FILL IS A SALE. 20 rounds of room takes 20 of a 60-box and
    // the rest is spilled on the floor; refusing unless the whole box fits
    // makes the shop say "you cannot buy ammunition because you nearly have
    // enough ammunition", which is the `full`-versus-`poor` mistake wearing a
    // different hat. `roomForAmmo()` answers "is there ANY room".
    apply: (player, e, ctx) => ctx.loadout.addAmmo(e.amount),
    describe: (e) => `+${e.amount} rounds`,
    // ⚠ NO `state`. A full pouch is `'full'`, never `'owned'` — ammunition is a
    // CONSUMABLE and you do not own it, you spend it. `'owned'` stays a state
    // nothing shipped can produce.
    full: 'Ammo pouch full',
  }),
});

/**
 * REFUSAL PRECEDENCE, as data. Lower wins, so a row that is both owned and full
 * reads as owned. ⚠ A new refusal state is a row HERE plus a `state` on a verb —
 * never a branch, and never a comparison against a specific effect type.
 * @type {Readonly<Record<string, number>>}
 */
const REFUSAL_RANK = Object.freeze({ owned: 0, full: 1 });

/** The one refusal string with words in it. */
const PROMPT_NO_KEEPER = 'Nobody is serving';
/**
 * The offer. Deliberately says nothing about WHAT is for sale — the old prompt
 * carried the item and the price because it WAS the offer; the panel is now
 * where the goods live, and a prompt that named one of six rows would be
 * choosing for the player.
 */
const PROMPT_SHOP = 'F — Shop';
/** Shared empty rows list, so a refusal allocates nothing. @type {ShopRow[]} */
const NO_ROWS = Object.freeze([]);

/**
 * Mirrors `EnemyManager`'s module-private `quantYaw`. Duplicated rather than
 * exported because it is three lines of arithmetic and exporting it would put a
 * renderer-facing detail on the manager's public surface; if the manager's ever
 * changes, this must change with it (`tests/shops.test.mjs` pins the keeper's
 * facing against a hand-computed value).
 * @param {number} yaw Radians, any range.
 * @returns {number} 0..255 — 1/256 of a turn.
 */
function quantYaw(yaw) {
  const u = yaw / TAU;
  return (Math.round((u - Math.floor(u)) * 256) & 255) >>> 0;
}

/**
 * @param {number} d A die in [0,1).
 * @param {number} lo @param {number} hi Inclusive.
 * @returns {number} An integer in `[lo, hi]`.
 */
function intOf(d, lo, hi) {
  const n = hi - lo + 1;
  if (n <= 1) return lo;
  const k = lo + Math.floor(d * n);
  return k > hi ? hi : k;
}

/**
 * The shop: ambient staff and customers while the player is in a room, and the
 * till they came for.
 *
 * @param {Object} o
 * @param {any} [o.manager] `EnemyManager`. Without one there are never NPCs and
 *   the till still works — which is exactly what every dev scene wants.
 * @param {any} o.interiors `createInteriors`' system. Read `plan` and `inside`
 *   ONLY; this file must never drive the door.
 * @param {any} [o.wallet] `createWallet`'s system. Without one there is no
 *   money in the world, so the till is INERT (a null prompt) rather than free.
 * @param {any} o.player For `position`, and for whatever the effect verbs reach
 *   for (`hurt(-n)`, `addArmour(n)`). Rebindable through `setPlayer` — Gameplay
 *   hot-swaps the Player when its sprite decodes, the same seam
 *   `Driving`/`Carjack`/`Interiors` have.
 * @param {any} [o.bus] `'shop:open'` / `'shop:state'` / `'shop:bought'` /
 *   `'shop:close'` out, `'shop:buy'` / `'shop:close'` in. ⚠ Without a bus
 *   NOTHING can open — the panel is the only way to buy now, and a panel nobody
 *   can be told about is a panel that would never close. So `canOpen()` requires
 *   one and the prompt is null without one; that is the promise rule, not a
 *   convenience.
 * @param {Readonly<Record<string, ShopCatalogueEntry>>} [o.catalogue] Defaults
 *   to {@link SHOP_CATALOGUE}. A real seam: the suites inject FABRICATED trades.
 * @param {Readonly<Record<string, ShopEffectVerb>>} [o.effects] Defaults to
 *   {@link SHOP_EFFECTS}. A real seam: the suites inject FABRICATED effect
 *   types, which is what proves nothing here is hard-coded.
 * @param {any} [o.loadout] The ownership seam, and it is SPENT — `Gameplay`
 *   passes the WEAPONS system here, which is what the `ammo` verb buys rounds
 *   into. Handed to the verbs as `ctx.loadout` and still defaulting to null, so
 *   a caller that has no weapons system simply loses the rows that need one.
 *   See the header.
 * @param {number[]} [o.civTypes] Manager type indices that are civilians. EMPTY
 *   (the default) means no NPCs at all — the system populates nothing and the
 *   till still serves, because a shop with no modelled keeper is a shop whose
 *   keeper was never killed.
 * @param {string|number} [o.seed] Level seed. Only used through the room's own
 *   `plan.seed`, so a shop's staff is a property of the shop.
 * @param {any} [o.factions] For `factions.CIVILIAN` (the team byte). Null => 0,
 *   which IS civilian in both the `TEAM` and the `FACTION` enums.
 * @returns {Object}
 */
export function createShops(o) {
  const {
    manager = null,
    interiors = null,
    wallet = null,
    bus = null,
    civTypes = [],
    seed = 0,
    factions = null,
    catalogue: table = SHOP_CATALOGUE,
    effects: verbs = SHOP_EFFECTS,
    loadout = null,
  } = o ?? {};
  let player = o?.player ?? null;

  if (!interiors) throw new Error('createShops: interiors is required');

  /**
   * The one context object every verb sees. Built once and FROZEN: it is
   * handed out on every evaluation, so a fresh object per call would be
   * garbage at the prompt scan's rate, and a mutable one would be a channel
   * two verbs could talk to each other through.
   * @type {ShopEffectCtx}
   */
  const effectCtx = Object.freeze({ loadout });

  /** The team byte every shop NPC carries. */
  const CIVILIAN = factions?.CIVILIAN ?? 0;
  /** Civilian type indices, snapshotted — a later mutation must not reshuffle
   *  a shop that was already populated. */
  const types = Array.isArray(civTypes) ? civTypes.slice() : [];

  // ── the machine ───────────────────────────────────────────────────────────
  /** The plan we last populated for, compared BY IDENTITY. @type {InteriorPlan|null} */
  let current = null;
  /** Manager slot of the shopkeeper, or -1 (no manager, no civ types, or the
   *  pool was full). */
  let keeper = -1;
  /**
   * True once a keeper we DID spawn has left the world. Distinct from
   * `keeper < 0`, which also covers "there was never one to kill" — a room with
   * no modelled staff serves, a room whose staff was shot does not.
   */
  let keeperLost = false;
  /**
   * The customers. Fixed shape per entry, appended at populate and spliced by
   * the despawn chain, so the per-step loop allocates nothing.
   * @type {Array<{slot: number, t: number, period: number, station: number}>}
   */
  const customers = [];
  /** Snapshot buffer for {@link clear} — see the `Missions#clearActors` note:
   *  despawning fires the chain, which splices the list being walked. */
  /** @type {number[]} */
  const dying = [];

  let disposed = false;
  let sales = 0;
  let spentTotal = 0;
  let populatedCount = 0;
  /**
   * The open panel's token, or null. IT IS THE OPEN FLAG — one variable, so
   * "is a panel up" and "which panel is up" can never disagree.
   * @type {string|null}
   */
  let openToken = null;
  /**
   * ⚠ A PLAIN COUNTER. Rule 4: no `Math.random()` anywhere in `src/`, and a
   * token is not a secret — it exists to reject a STALE answer (the
   * `mission:cutscene:end` idiom, `ui/hud/Cutscene.jsx`), and monotonic is the
   * strongest thing an ordering test can ask for.
   */
  let tokenSeq = 0;
  /** Dice drawn by the last {@link populate}. The suite's handle on the
   *  fixed-dice discipline. */
  let lastDice = 0;

  // ── the despawn chain (see the header) ────────────────────────────────────
  /**
   * Whatever owned `manager.onDespawn` when we were built — `Gameplay` wires
   * the shadow-blob pool plus the population and mission directors there. We
   * are a link in that chain, never a replacement for it.
   * @type {Function|null}
   */
  const prevOnDespawn = manager && typeof manager.onDespawn === 'function' ? manager.onDespawn : null;

  /**
   * A manager slot went free, for ANY reason: shot by the player, killed in a
   * crossfire, cleared by a level teardown, or despawned by us. Drop our
   * bookkeeping FIRST so a slot the pool hands straight back out can never be
   * mistaken for one of ours.
   * @param {number} slot @param {any} m
   */
  const onSlotFreed = (slot, m) => {
    if (slot === keeper) {
      keeper = -1;
      keeperLost = true;
    }
    for (let i = 0; i < customers.length; i++) {
      if (customers[i].slot !== slot) continue;
      customers.splice(i, 1);
      break;
    }
    prevOnDespawn?.(slot, m);
  };
  if (manager) manager.onDespawn = onSlotFreed;

  // ── helpers ───────────────────────────────────────────────────────────────

  /**
   * @param {number} slot
   * @returns {boolean} Still someone. DYING counts as GONE — a keeper in his
   *   death shrink is on the floor, and offering to buy a sandwich off him is
   *   the sort of thing a player screenshots. Same reading as
   *   `Carjack.js#seated`.
   */
  const alive = (slot) => slot >= 0 && !!manager && manager.isLive(slot)
    && manager.state[slot] !== STATE.DYING;

  /**
   * @returns {boolean} There is somebody behind the counter. A room that never
   *   had a modelled keeper (no manager, no civilian types, a full pool) serves
   *   perfectly well — the refusal is for a keeper who WAS there and is not any
   *   more.
   */
  const served = () => !keeperLost && (keeper < 0 || alive(keeper));

  /**
   * Puts a slot back on the room's floor. The manager re-samples `y` from the
   * OUTDOOR height function every step (`ctx.heightAt` is the terrain's), which
   * would drag a shop's crowd up through the pavement and draw them standing
   * inside the building they are supposed to be under.
   *
   * ⚠ This is a floor, not a fix: the manager also resolves these actors
   * against the OUTDOOR collision grid, so a customer can walk through the
   * room's walls. The real repair is for the environment `Gameplay` hands the
   * manager to follow the player indoors, which is that file's to make.
   * @param {number} slot @param {number} floorY
   */
  const pinY = (slot, floorY) => {
    if (manager?.y) manager.y[slot] = floorY;
  };

  // ── populate / clear ──────────────────────────────────────────────────────

  /**
   * Everyone we own, gone. Idempotent, correct from a standing start, and safe
   * to call from `dispose()`, from the player dying and from the identity watch
   * noticing that `Interiors#abort` dropped the plan.
   *
   * ⚠ The list is emptied BEFORE the despawns, not after: `manager.despawn`
   * fires {@link onSlotFreed}, which splices the very array a naive loop would
   * be walking — the `Missions#clearActors` trap, one file over.
   *
   * ⚠ AND IT CLOSES THE PANEL, which makes this THE invalidation door: `dispose`
   * and the identity watch are the only two callers and both mean "the room is
   * gone", so routing the close through here is the same move `Interiors#abort`
   * makes by going through `swapOut` rather than repeating it. A shop panel left
   * up over a torn-down world sits on screen with nothing left to answer it —
   * the unrecoverable state `ui/hud/Cutscene.jsx` records finding the hard way.
   */
  const clear = () => {
    close();
    dying.length = 0;
    if (keeper >= 0) dying.push(keeper);
    for (let i = 0; i < customers.length; i++) dying.push(customers[i].slot);
    keeper = -1;
    keeperLost = false;
    customers.length = 0;
    current = null;
    for (let i = 0; i < dying.length; i++) manager?.despawn?.(dying[i]);
    dying.length = 0;
  };

  /**
   * The room's crowd, from the room's own seed.
   *
   * THE DICE, in this exact order and all of them rolled before the first
   * rejection:
   *
   * ```
   *   1              the keeper's type
   *   1              how many customers (int, inclusive)
   *   2 per customer type, then dwell  ── rolled for EVERY customer, including
   *                                       one there is no station for
   * ```
   *
   * A customer takes the browse station at its OWN INDEX — no die, so adding a
   * stand point to a room cannot move the customers already standing in it, and
   * two customers never share a spot. Customers past the last station are the
   * ones dropped, after their dice.
   *
   * The dwell die does double duty: it is the customer's phase (so nobody moves
   * on the same step as anybody else) AND its personal period inside
   * `browseDwell ± browseJitter`. One die, both jobs, and no rng at all after
   * this function returns — a per-step draw here would make the crowd depend on
   * how long the player stood in the doorway.
   *
   * @param {InteriorPlan} plan
   */
  const populate = (plan) => {
    current = plan;
    populatedCount++;
    lastDice = 0;
    if (!manager || types.length === 0) return;

    // Salted `'shop'` so this stream can never collide with `'interior'` (the
    // room's own furniture dice) or the building's, and keyed on the ROOM's
    // seed so a given shop always has the same staff, however many times the
    // player walks in and out.
    const rng = mulberry32((hashSeed('shop') + (plan.seed >>> 0)) >>> 0);
    const die = () => {
      lastDice++;
      return rng();
    };
    const typeOf = (d) => types[Math.min(types.length - 1, (d * types.length) | 0)];

    const st = plan.stations ?? null;
    const floorY = plan.floorY ?? 0;

    // ── the keeper ──
    const keeperType = typeOf(die());
    if (st?.keeper) {
      const k = st.keeper;
      const slot = manager.spawn(keeperType, k.x, k.z, floorY, CIVILIAN);
      if (slot >= 0) {
        manager.setBrain(slot, BRAIN.CIVILIAN);
        // Goaled at his own feet: `needsGoal` reads true for a civilian standing
        // on its waypoint, but nobody is listening — the population director
        // hands errands to slots its `owned[]` byte marks, and ours is not one.
        // So he simply stands there, which is what a shopkeeper does.
        manager.setGoal(slot, k.x, k.z);
        if (manager.facing) manager.facing[slot] = quantYaw(k.yaw ?? 0);
        pinY(slot, floorY);
        keeper = slot;
      }
    }

    // ── the customers ──
    const n = intOf(die(), SHOP.minCustomers, SHOP.maxCustomers);
    const browse = Array.isArray(st?.browse) ? st.browse : [];
    for (let i = 0; i < n; i++) {
      // BOTH dice first, unconditionally — see the header.
      const ti = typeOf(die());
      const d = die();
      if (i >= browse.length) continue; // nowhere to stand: dropped, dice spent
      const b = browse[i];
      const slot = manager.spawn(ti, b.x, b.z, floorY, CIVILIAN);
      if (slot < 0) continue; // pool full: same story, and the dice still went
      manager.setBrain(slot, BRAIN.CIVILIAN);
      manager.setGoal(slot, b.x, b.z);
      pinY(slot, floorY);
      const period = SHOP.browseDwell + (d * 2 - 1) * SHOP.browseJitter;
      customers.push({ slot, t: d * period, period, station: i });
    }
  };

  // ── the catalogue ─────────────────────────────────────────────────────────

  /** @param {ShopItem} item @returns {string} A row always has something to
   *  print; an item with no label falls back to its id rather than to
   *  `undefined`. */
  const labelOf = (item) => (typeof item.label === 'string' && item.label ? item.label : item.id);

  /**
   * The DELIVERABLE effects of one item, paired with their verbs. The single
   * expression of the forward-compat idiom in the header: {@link rowFor} uses it
   * to decide whether a row exists at all, and {@link buy} uses the SAME
   * function to decide what to apply, so a row can never be sold an effect it
   * did not advertise.
   *
   * @param {ShopItem} item
   * @returns {Array<{e: ShopEffectSpec, verb: ShopEffectVerb}>} Possibly empty.
   */
  const liveEffectsOf = (item) => {
    /** @type {Array<{e: ShopEffectSpec, verb: ShopEffectVerb}>} */
    const live = [];
    const specs = Array.isArray(item?.effects) ? item.effects : [];
    for (let i = 0; i < specs.length; i++) {
      const e = specs[i];
      const verb = e && typeof e.type === 'string' ? verbs?.[e.type] : null;
      // Unknown type, or a verb with nowhere to put the effect. NOT an error:
      // a `weapon` row may be authored years before `Player.giveWeapon` exists.
      if (!verb || typeof verb.apply !== 'function') continue;
      if (typeof verb.available === 'function' && verb.available(player, e, effectCtx) !== true) continue;
      live.push({ e, verb });
    }
    return live;
  };

  /**
   * One catalogue item, resolved against the live world into something a panel
   * can draw without knowing anything about this game.
   *
   * PRECEDENCE, and it is the header's: undeliverable → DROPPED (null), then
   * the refusal states by {@link REFUSAL_RANK} (`owned` before `full`), then
   * `poor`, else `buy`. Affordability is asked LAST on purpose — "you cannot
   * afford a vest you are already wearing" is the wrong sentence.
   *
   * @param {ShopItem} item
   * @returns {ShopRow|null} Null means the row does not exist.
   */
  const rowFor = (item) => {
    if (!item || typeof item.id !== 'string') return null;
    // A price is not optional: an item nobody can be charged for is an item the
    // till cannot ring up, and 0 is a legal (free) price.
    if (typeof item.price !== 'number' || !Number.isFinite(item.price) || item.price < 0) return null;

    const live = liveEffectsOf(item);
    if (live.length === 0) return null; // ⚠ absent, never greyed — see the header

    /** @type {string[]} */
    const parts = [];
    /** @type {{state: string, reason: string|null}|null} */
    let refusal = null;
    let useful = false;
    for (let i = 0; i < live.length; i++) {
      const { e, verb } = live[i];
      if (typeof verb.describe === 'function') parts.push(String(verb.describe(e)));
      // A verb with no `can` has no precondition — buying it always does
      // something. Only an explicit false refuses.
      if (typeof verb.can !== 'function' || verb.can(player, e, effectCtx) === true) {
        useful = true;
        continue;
      }
      const state = REFUSAL_RANK[verb.state] != null ? verb.state : 'full';
      if (!refusal || REFUSAL_RANK[state] < REFUSAL_RANK[refusal.state]) {
        refusal = { state, reason: typeof verb.full === 'string' ? verb.full : null };
      }
    }

    // ONE deliverable effect that would do something is enough: a row that
    // heals AND armours is worth buying at full health if the vest is empty.
    if (!useful) {
      const r = refusal ?? { state: 'full', reason: null };
      return { id: item.id, label: labelOf(item), price: item.price, effect: parts.join(' · '), state: r.state, reason: r.reason };
    }
    if (!wallet.canAfford(item.price)) {
      return { id: item.id, label: labelOf(item), price: item.price, effect: parts.join(' · '), state: 'poor', reason: `Need $${item.price}` };
    }
    return { id: item.id, label: labelOf(item), price: item.price, effect: parts.join(' · '), state: 'buy', reason: null };
  };

  /**
   * THE one evaluation. `prompt`, `canOpen`, `open`, `press` and `buy` are all
   * callers of this, which is what makes "the offer matches what the key does"
   * STRUCTURAL — see the header. It reads the world and nothing else: no
   * caching, no scratch reuse (a caller that kept the record would be holding a
   * snapshot of a world that has moved), and no side effects at all.
   *
   * @returns {{canOpen: boolean, prompt: string|null, kind: string|null,
   *   title: string|null, rows: ShopRow[], srcs: ShopItem[]}}
   *   `srcs[i]` is the catalogue item `rows[i]` was built from — internal, and
   *   the reason {@link buy} never has to trust an id or re-find an item under
   *   different rules.
   */
  const evaluate = () => {
    const none = { canOpen: false, prompt: null, kind: null, title: null, rows: /** @type {ShopRow[]} */ (NO_ROWS), srcs: [] };
    if (disposed || !player || !wallet || !bus) return none;
    if (player.dead) return none;
    // Mid-transition the player is behind a black curtain standing on the entry
    // mat; `Interiors#press` has already refused, and a panel opened during the
    // fade would be a shop nobody walked into. Same `busy()` idiom, one file over.
    if (interiors.locked === true) return none;

    const plan = interiors.plan ?? null;
    if (!plan || !interiors.inside) return none;
    const till = plan.stations?.till;
    if (!till) return none;

    const dx = till.x - player.position.x;
    const dz = till.z - player.position.z;
    if (dx * dx + dz * dz > SHOP.tillReach * SHOP.tillReach) return none;

    if (!served()) return { ...none, prompt: PROMPT_NO_KEEPER };

    // ⚠ `trade ?? kind` — the forward seam. See the header: `trade` is what a
    // room SELLS, `kind` is what it was BUILT as, and nothing sets the former
    // yet.
    const key = plan.trade ?? plan.kind ?? null;
    const entry = typeof key === 'string' ? table?.[key] : null;
    if (!entry) return none; // a room with no catalogue entry sells nothing, silently

    const items = Array.isArray(entry.items) ? entry.items : [];
    /** @type {ShopRow[]} */
    const rows = [];
    /** @type {ShopItem[]} */
    const srcs = [];
    for (let i = 0; i < items.length; i++) {
      const row = rowFor(items[i]);
      if (!row) continue;
      rows.push(row);
      srcs.push(items[i]);
    }
    // ⚠ A shop with rows the player cannot afford or does not need STILL OPENS —
    // that is the inversion in the header. Only a shop with NOTHING renderable
    // refuses, because there would be nothing behind the door.
    if (rows.length === 0) return none;

    const title = typeof entry.title === 'string' && entry.title ? entry.title : key;
    return { canOpen: true, prompt: PROMPT_SHOP, kind: key, title, rows, srcs };
  };

  /**
   * @param {ReturnType<typeof evaluate>} ev
   * @returns {ShopSnapshot} What the panel draws. Always a real object — an
   *   empty `items` is a shop that has closed under the panel's feet, which is
   *   a thing a renderer can handle and a null is not.
   */
  const snapshotOf = (ev) => ({
    token: openToken,
    kind: ev.kind,
    title: ev.title,
    cash: wallet?.balance ?? 0,
    items: ev.rows,
  });

  // ── the panel ─────────────────────────────────────────────────────────────

  /**
   * Opens the shop panel. The token is minted FIRST so the `'shop:open'`
   * payload already carries it — a panel that had to wait for a second event to
   * learn its own token could not answer the first one.
   * @returns {boolean} A panel actually opened.
   */
  const open = () => {
    if (disposed || openToken) return false; // ⚠ refuses while already open
    const ev = evaluate();
    if (!ev.canOpen) return false;
    openToken = `shop:${++tokenSeq}`;
    bus.emit?.('shop:open', snapshotOf(ev));
    return true;
  };

  /**
   * Closes it. Idempotent — a second call is a no-op and emits nothing.
   *
   * ⚠ `openToken` is cleared BEFORE the emit, which is what makes our own
   * subscription on this channel unable to hear us: the handler's first test is
   * that a panel is open. That is the structural version of `Cutscene.jsx`'s
   * `sentRef` latch, and it is why this file can own both ends of a two-way
   * channel without a flag.
   * @returns {boolean} A panel was open and is not now.
   */
  const close = () => {
    if (!openToken) return false;
    const token = openToken;
    openToken = null;
    bus?.emit?.('shop:close', { token });
    return true;
  };

  /**
   * THE ONLY PURCHASE PATH — the bus handler, the panel and any test all come
   * through here, and it re-runs {@link evaluate} rather than trusting anything
   * it was handed. An id from a frozen panel is an id from a world that may
   * have moved.
   *
   * @param {string} id
   * @returns {boolean} True only when money changed hands.
   */
  const buy = (id) => {
    const ev = evaluate();
    if (!ev.canOpen) return false;

    let k = -1;
    for (let i = 0; i < ev.rows.length; i++) {
      if (ev.rows[i].id !== id) continue;
      k = i;
      break;
    }
    // Not a row we are offering, or a row we are offering greyed. Either way
    // the panel is showing something this world does not support.
    if (k < 0 || ev.rows[k].state !== 'buy') return false;

    const item = ev.srcs[k];
    if (!wallet.spend(item.price)) return false; // belt and braces: canAfford said yes

    // EVERY deliverable effect, in authored order, through the same resolution
    // that built the row. An undeliverable one was never described and is never
    // applied.
    const live = liveEffectsOf(item);
    for (let i = 0; i < live.length; i++) live[i].verb.apply(player, live[i].e, effectCtx);

    sales++;
    spentTotal += item.price;
    // ⚠ BOTH KEYS, because on the forward seam they are two different questions
    // and one field cannot answer both. `kind` is what the room was BUILT as
    // (its fixtures, its stock, its street sign — baked); `trade` is what it
    // SOLD, which is the catalogue key the snapshot carries. They hold the same
    // string for every room in the game today, so nothing can tell them apart
    // until a plan sets `trade` — and on that day a consumer that guessed would
    // be silently wrong about the only case the seam exists for. `trade` is the
    // one to read. ⚠ Do not name a real trade in this comment: the suite's
    // genericity guard greps this function's body for one, and it is right to.
    bus.emit?.('shop:bought', {
      kind: interiors.plan?.kind ?? null,
      trade: ev.kind,
      item: item.id,
      label: labelOf(item),
      price: item.price,
      cash: wallet.balance,
    });

    // ⚠ SYNCHRONOUS, and it has to be. The React panel freezes the sim with
    // `loop.setTimeScale(0)` (the `Cutscene.jsx` idiom), so `update()` is NOT
    // running while the panel is open: a state refresh deferred to the next
    // step would arrive after the player closed the panel, i.e. never. The
    // player's cash, hp and armour all just moved and every row's state may
    // have moved with them, so the snapshot is re-derived, not patched.
    bus.emit?.('shop:state', snapshotOf(evaluate()));
    return true;
  };

  /**
   * THE TILL KEY. `Gameplay` offers F here after `Interiors` and before
   * `Carjack`; a door mat and a till are never the same spot, and a car is never
   * inside a room, so the three can only ever disagree about a press none of
   * them wants.
   *
   * ⚠ It OPENS now — it does not sell. There is no key that buys anything any
   * more, and that is the point of the panel: a purchase is a choice between
   * rows, which a single key cannot express.
   * @returns {boolean} True only when a panel actually opened.
   */
  const press = () => open();

  // ── the bus: this file owns BOTH ends ─────────────────────────────────────
  /** @type {Function[]} */
  const unsubs = [];
  if (bus?.on) {
    /**
     * ⚠ TOKEN-GATED, and that is what the token is FOR (`Cutscene.jsx`): a
     * panel that outlived its room, a double-click, a second panel — every one
     * of them arrives here carrying a token that is not the open one, and every
     * one of them is dropped.
     * @param {any} p
     */
    const onBuy = (p) => {
      if (!openToken || p?.token !== openToken) return;
      if (typeof p?.id !== 'string') return;
      buy(p.id);
    };
    /**
     * The panel's own close. We hear only SOMEBODY ELSE's — {@link close}
     * clears the token before it emits, so our own close is already stale by
     * the time it arrives here.
     * @param {any} p
     */
    const onClose = (p) => {
      const token = typeof p === 'string' ? p : p?.token;
      if (!openToken || token !== openToken) return;
      close();
    };
    // `createBus().on` returns its own unsubscriber; the `off(type, fn)`
    // fallback is for a duck-typed bus in a suite that does not.
    const offBuy = bus.on('shop:buy', onBuy);
    const offClose = bus.on('shop:close', onClose);
    unsubs.push(typeof offBuy === 'function' ? offBuy : () => bus.off?.('shop:buy', onBuy));
    unsubs.push(typeof offClose === 'function' ? offClose : () => bus.off?.('shop:close', onClose));
  }

  // ── the step ──────────────────────────────────────────────────────────────

  /**
   * One fixed step. Runs AFTER `Interiors#update` — it reads that system's
   * plan, and reading last step's would populate a room one step late and clear
   * one step after the player was already outdoors.
   * @param {number} dt
   */
  const update = (dt) => {
    if (disposed) return;

    // ⚠ A DEAD PLAYER CLOSES THE PANEL, and it is checked here rather than left
    // to the identity watch below. Death normally reaches us as
    // `Interiors#abort()` dropping the plan, which the watch catches — but that
    // is a chain through another system, and this is the one state where the
    // player cannot dismiss the panel themselves.
    if (openToken && player?.dead) close();

    // ── the identity watch: the whole lifecycle, in four lines ──
    const plan = interiors.plan ?? null;
    if (plan !== current) {
      clear();
      if (plan) populate(plan);
    }
    if (!current || !(dt > 0)) return;

    const floorY = current.floorY ?? 0;
    const browse = Array.isArray(current.stations?.browse) ? current.stations.browse : [];
    if (keeper >= 0) pinY(keeper, floorY);

    for (let i = 0; i < customers.length; i++) {
      const c = customers[i];
      pinY(c.slot, floorY);
      if (browse.length < 2) continue; // one station: nowhere to move to
      c.t += dt;
      if (c.t < c.period) continue;
      c.t -= c.period;
      // Round the room rather than a fresh draw: deterministic, allocation-free
      // and — the point — independent of how long the player has been standing
      // in here, which no rng-per-move scheme can be.
      c.station = (c.station + 1) % browse.length;
      const b = browse[c.station];
      manager?.setGoal?.(c.slot, b.x, b.z);
    }
  };

  return {
    name: 'shops',
    update,
    press,
    open,
    close,
    buy,

    /**
     * @returns {boolean} A panel could open RIGHT NOW. The same evaluation the
     *   prompt reads, exposed so a caller (a dev scene, the suite, a HUD) can
     *   ask without provoking one.
     */
    canOpen() {
      return evaluate().canOpen;
    },

    /**
     * @returns {ShopSnapshot} What the panel draws, re-derived from the live
     *   world. Never null: outside a shop it is an empty `items` with a null
     *   `kind`, because "the shop closed under you" is a state a renderer can
     *   draw and a null is not.
     */
    catalogue() {
      return snapshotOf(evaluate());
    },

    /** @returns {boolean} A panel is up. While it is, {@link prompt} is null —
     *  the HUD must not offer a key for a thing that is already on screen. */
    get panelOpen() {
      return openToken !== null;
    },

    /**
     * Gameplay's player hot-swap points us at the replacement actor. Nothing
     * else to do: this system keeps no state about the player, only about the
     * room.
     * @param {any} p
     */
    setPlayer(p) {
      player = p ?? null;
    },

    /**
     * @returns {string|null} What the HUD should show. Handed to `Interiors` as
     *   its `extraPrompt`, so there is exactly ONE publisher on the
     *   edge-detected `'hud:prompt'` channel.
     *
     *   THREE outcomes, and the middle one is the whole inversion in the
     *   header: {@link PROMPT_SHOP} whenever a panel could open — INCLUDING a
     *   shop where every row is unaffordable or useless, because saying so is
     *   what the panel is for — {@link PROMPT_NO_KEEPER} when the keeper is
     *   dead, and null otherwise.
     */
    get prompt() {
      if (openToken) return null;
      return evaluate().prompt;
    },

    /** @returns {boolean} A room's crowd is currently populated. True even for
     *  a room that yielded nobody (no civilian types, an empty roster) — it
     *  means "this plan has been processed", which is what the lifecycle is
     *  about. */
    get active() {
      return current !== null;
    },

    /** @returns {number[]} The manager slots this system owns, keeper first. A
     *  copy — the caller must not be able to splice our bookkeeping. */
    slots() {
      const out = keeper >= 0 ? [keeper] : [];
      for (let i = 0; i < customers.length; i++) out.push(customers[i].slot);
      return out;
    },

    /** @returns {{kind: string|null, npcs: number, keeper: number,
     *   sales: number, spent: number, populated: number, dice: number,
     *   wallet: boolean, open: boolean}} */
    stats() {
      return {
        /** The room's BUILD kind, unchanged — `DebugPanel` reads it to name the
         *  room, and the trade key is the panel's business, not the debug
         *  readout's. */
        kind: current?.kind ?? null,
        npcs: (keeper >= 0 ? 1 : 0) + customers.length,
        keeper,
        sales,
        spent: spentTotal,
        /** How many rooms have been populated this session — a counter, not a
         *  flag; `active` is the flag. */
        populated: populatedCount,
        dice: lastDice,
        wallet: !!wallet,
        /** A panel is up — which also means the sim is frozen under it. */
        open: openToken !== null,
      };
    },

    /**
     * Idempotent. Despawns everything it owns and unhooks the despawn chain —
     * but ONLY if we are still the link that is wired in. Somebody who
     * reassigned `manager.onDespawn` after us owns it now, and restoring our
     * captured predecessor over the top of theirs would silently delete a
     * director's bookkeeping.
     *
     * ⚠ `clear()` runs FIRST and before `disposed` is set, because it is what
     * emits `'shop:close'` — a teardown that left the panel on screen would
     * leave it over a world with nothing left to answer it, and the emit must
     * happen while we can still make one.
     */
    dispose() {
      if (disposed) return;
      clear();
      disposed = true;
      for (let i = 0; i < unsubs.length; i++) unsubs[i]();
      unsubs.length = 0;
      if (manager && manager.onDespawn === onSlotFreed) manager.onDespawn = prevOnDespawn;
    },
  };
}
